import type { ButtonHTMLAttributes } from 'react';
import { useId, useState } from 'react';
import { variantColors, type ButtonVariant } from './variantColors';
import { useClickWithSound } from '@/shared/hooks/useClickWithSound';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?: ButtonVariant;
}

// Shadow/base layer path
const SHADOW_PATH =
  'M40.44 2H190.65C211.87 2 229.09 19.23 229.09 40.44V224.75C229.09 245.97 211.86 263.19 190.65 263.19H40.44C19.22 263.19 2 245.96 2 224.75V40.44C2 19.23 19.23 2 40.44 2Z';

// Black border path (outer and inner combined)
const BORDER_PATH =
  'M190.66 4C210.79 4 227.1 20.32 227.1 40.44V224.75C227.1 244.88 210.78 261.19 190.66 261.19H40.44C20.31 261.19 4 244.87 4 224.75V40.44C4 20.32 20.32 4 40.44 4H190.65M190.66 0H40.44C18.14 0 0 18.14 0 40.44V224.75C0 247.05 18.14 265.19 40.44 265.19H190.65C212.95 265.19 231.09 247.05 231.09 224.75V40.44C231.09 18.14 212.95 0 190.65 0H190.66Z';

// Main button gradient path
const MAIN_PATH =
  'M190.66 4H40.44C20.3147 4 4 20.3147 4 40.44V208.37C4 228.495 20.3147 244.81 40.44 244.81H190.66C210.785 244.81 227.1 228.495 227.1 208.37V40.44C227.1 20.3147 210.785 4 190.66 4Z';

export function Button({
  children,
  className = '',
  variant = 'blue',
  disabled,
  onClick,
  ...props
}: ButtonProps) {
  const [isPressed, setIsPressed] = useState(false);
  const gradientId = useId();

  const colors = variantColors[variant];
  const activePress = isPressed && !disabled;
  const handleClick = useClickWithSound(onClick, disabled);

  return (
    <button
      className={`relative transition-all duration-150 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      style={{ containerType: 'size' }}
      onMouseDown={() => {
        setIsPressed(true);
      }}
      onMouseUp={() => {
        setIsPressed(false);
      }}
      onMouseLeave={() => {
        setIsPressed(false);
      }}
      onTouchStart={() => {
        setIsPressed(true);
      }}
      onTouchEnd={() => {
        setIsPressed(false);
      }}
      onClick={handleClick}
      disabled={disabled}
      {...props}
    >
      <svg
        className="block size-full transition-transform duration-150"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 232 266"
        style={{ transform: activePress ? 'translateY(2px)' : 'none' }}
        aria-hidden="true"
      >
        {/* Shadow/base layer */}
        <path d={SHADOW_PATH} fill={colors.shadow} />
        {/* Black border */}
        <path d={BORDER_PATH} fill="#000" />
        {/* Main gradient button */}
        <path d={MAIN_PATH} fill={`url(#${gradientId})`} />
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={gradientId}
            x1="115.55"
            x2="115.55"
            y1="244.81"
            y2="4"
          >
            <stop stopColor={colors.gradient.start} />
            <stop offset="0.49" stopColor={colors.gradient.middle} />
            <stop offset="1" stopColor={colors.gradient.end} />
          </linearGradient>
        </defs>
      </svg>

      <span
        className="absolute inset-0 flex items-center justify-center text-white font-semibold pointer-events-none transition-transform duration-150"
        style={{ transform: activePress ? 'translateY(2px)' : 'none' }}
      >
        {children}
      </span>
    </button>
  );
}

export default Button;
